import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Select,
  Checkbox,
  Descriptions,
  Alert,
  Row,
  Col,
  DatePicker,
  TimePicker,
  Form,
  Empty,
} from "antd";
import {
  PlusOutlined,
  GroupOutlined,
  CalendarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  getBatchSuggestions,
  createBatch,
  getBatches,
  scheduleBatch,
  getAllMachines,
} from "../../api/schedulingApi";
import checkTokenExp from "../../utils/checkTokenExp";

const { Option } = Select;

/**
 * BatchManager — create & manage production batches.
 *
 * Left panel: suggestions from §7.2 GET batch-suggestions/:stageCode
 * Right panel: existing batches from §7.4 GET batch (paginated)
 *
 * Uses stageCode (e.g. "corrugation") for suggestions; stageId for
 * creating batches.  The suggestions response carries both.
 */

// Hard-coded production stages matching the backend
const STAGES = [
  { code: "corrugation", name: "Corrugation" },
  { code: "sheet_cutting", name: "Sheet Cutting" },
  { code: "pasting", name: "Pasting" },
  { code: "printing", name: "Printing" },
  { code: "casting", name: "Casting" },
  { code: "rotary", name: "Rotary" },
  { code: "joining", name: "Joining" },
];

const BatchManager = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [selectedStageCode, setSelectedStageCode] = useState("corrugation");
  const [suggestions, setSuggestions] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchPagination, setBatchPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  // Create batch modal
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedBatchSet, setSelectedBatchSet] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  // Schedule batch modal
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleForm] = Form.useForm();

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    fetchMachines();
    fetchBatches();
  }, []);

  useEffect(() => {
    if (selectedStageCode) fetchSuggestions();
  }, [selectedStageCode]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }
  checkTokenExp(accessToken);

  const fetchMachines = async () => {
    try {
      const data = await getAllMachines({ status: "operational" });
      setMachines(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to load machines", error);
    }
  };

  const fetchBatches = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getBatches({ page, limit: 10 });
      setBatches(data.batches || data.data || []);
      if (data.pagination) {
        setBatchPagination(data.pagination);
      }
    } catch (error) {
      toast.error("Failed to load batches");
    }
    setLoading(false);
  };

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const data = await getBatchSuggestions(selectedStageCode);
      setSuggestions(data.suggestions || data);
    } catch (error) {
      toast.error("Failed to load batch suggestions");
      setSuggestions(null);
    }
    setLoading(false);
  };

  // ── Create Batch ───────────────────────────────────────────
  const openCreateModal = (batchSet) => {
    setSelectedBatchSet(batchSet);
    setSelectedJobIds(batchSet.jobs.map((j) => j.id));
    setSelectedMachine(null);
    setCreateModalVisible(true);
  };

  const handleCreateBatch = async () => {
    if (selectedJobIds.length < 2) {
      toast.warning("Select at least 2 jobs to create a batch");
      return;
    }

    setCreateLoading(true);
    try {
      await createBatch({
        stage_id: selectedStageCode, // backend accepts stage code as stage_id
        job_ids: selectedJobIds,
        machine_id: selectedMachine || undefined,
      });
      toast.success("Batch created successfully");
      setCreateModalVisible(false);
      setSelectedBatchSet(null);
      fetchBatches();
      fetchSuggestions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create batch"
      );
    }
    setCreateLoading(false);
  };

  // ── Schedule Batch ─────────────────────────────────────────
  const openScheduleModal = (batch) => {
    setSelectedBatch(batch);
    scheduleForm.resetFields();
    setScheduleModalVisible(true);
  };

  const handleScheduleBatch = async (values) => {
    setScheduleLoading(true);
    try {
      await scheduleBatch(selectedBatch.id, {
        machine_id: values.machine_id,
        scheduled_date: values.scheduled_date.format("YYYY-MM-DD"),
        start_time: values.start_time.format("HH:mm"),
      });
      toast.success("Batch scheduled successfully");
      setScheduleModalVisible(false);
      setSelectedBatch(null);
      fetchBatches();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to schedule batch"
      );
    }
    setScheduleLoading(false);
  };

  // ── Batch table columns ────────────────────────────────────
  const batchColumns = [
    {
      title: "Batch #",
      dataIndex: "batch_number",
      key: "batch_number",
      width: 160,
    },
    {
      title: "Stage",
      key: "stage",
      width: 120,
      render: (_, record) => (
        <Tag>{record.stage?.name || record.stage_code || "-"}</Tag>
      ),
    },
    {
      title: "Roll Size",
      dataIndex: "roll_size",
      key: "roll_size",
      width: 80,
      render: (rs) => (rs ? `${rs}"` : "-"),
    },
    {
      title: "Jobs",
      key: "jobs",
      width: 80,
      render: (_, record) => (
        <Tag color="blue">{record._count?.jobs || record.jobs?.length || 0}</Tag>
      ),
    },
    {
      title: "Total Qty",
      dataIndex: "total_quantity",
      key: "total_quantity",
      width: 100,
      render: (qty) => (qty ? qty.toLocaleString() : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const colors = {
          created: "default",
          scheduled: "cyan",
          in_progress: "blue",
          completed: "green",
        };
        return (
          <Tag color={colors[status] || "default"}>
            {(status || "unknown").toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Machine",
      key: "machine",
      width: 130,
      render: (_, record) =>
        record.machine?.name || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          {record.status === "created" && (
            <Button
              size="small"
              type="primary"
              icon={<CalendarOutlined />}
              onClick={() => openScheduleModal(record)}
            >
              Schedule
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      <Row gutter={16}>
        {/* ── Left: Batch Suggestions ──────────────────────── */}
        <Col xs={24} lg={12}>
          <Card
            title="Batch Suggestions"
            extra={
              <Space>
                <Select
                  style={{ width: 150 }}
                  value={selectedStageCode}
                  onChange={setSelectedStageCode}
                  size="small"
                >
                  {STAGES.map((s) => (
                    <Option key={s.code} value={s.code}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchSuggestions}
                  loading={loading}
                  size="small"
                >
                  Refresh
                </Button>
              </Space>
            }
          >
            {suggestions?.batchable_sets?.length > 0 ? (
              suggestions.batchable_sets.map((set, idx) => (
                <Card
                  key={idx}
                  size="small"
                  style={{ marginBottom: 12 }}
                  title={
                    <Space>
                      <GroupOutlined />
                      <span>
                        RS: {set.roll_size}" | {set.jobs.length} jobs |{" "}
                        {set.total_quantity?.toLocaleString()} pcs
                      </span>
                      {set.flute_type && (
                        <Tag color="geekblue">{set.flute_type}-Flute</Tag>
                      )}
                    </Space>
                  }
                  extra={
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => openCreateModal(set)}
                    >
                      Create Batch
                    </Button>
                  }
                >
                  <div style={{ maxHeight: 100, overflow: "auto" }}>
                    {set.jobs.map((job) => (
                      <Tag key={job.id} style={{ marginBottom: 4 }}>
                        <Link to={`/job/${job.id}`}>{job.job_number}</Link>{" "}
                        ({job.quantity?.toLocaleString()})
                      </Tag>
                    ))}
                  </div>
                </Card>
              ))
            ) : (
              <Alert
                message="No batchable jobs found for this stage"
                description="Jobs with matching roll size, cutting size and flute type can be batched together for efficient production."
                type="info"
                showIcon
              />
            )}

            {suggestions?.single_jobs?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ color: "#888" }}>
                  Single Jobs (cannot batch)
                </h4>
                {suggestions.single_jobs.map((job) => (
                  <Tag key={job.id} style={{ marginBottom: 4 }}>
                    <Link to={`/job/${job.id}`}>{job.job_number}</Link>
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* ── Right: Existing Batches ──────────────────────── */}
        <Col xs={24} lg={12}>
          <Card
            title="Production Batches"
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchBatches()}
                loading={loading}
                size="small"
              >
                Refresh
              </Button>
            }
          >
            <Table
              columns={batchColumns}
              dataSource={batches}
              rowKey="id"
              loading={loading}
              pagination={{
                current: batchPagination.page,
                pageSize: batchPagination.limit,
                total: batchPagination.total,
                onChange: (p) => fetchBatches(p),
                showSizeChanger: false,
                size: "small",
              }}
              size="small"
              scroll={{ x: 750 }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Create Batch Modal ─────────────────────────────── */}
      <Modal
        title="Create Production Batch"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          setSelectedBatchSet(null);
        }}
        onOk={handleCreateBatch}
        okText="Create Batch"
        confirmLoading={createLoading}
        width={600}
      >
        {selectedBatchSet && (
          <div>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Roll Size">
                {selectedBatchSet.roll_size}"
              </Descriptions.Item>
              <Descriptions.Item label="Cutting Size">
                {selectedBatchSet.cutting_size
                  ? `${selectedBatchSet.cutting_size}"`
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Flute">
                {selectedBatchSet.flute_type || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Total Quantity" span={3}>
                {selectedBatchSet.total_quantity?.toLocaleString()} pcs
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 16 }}>
              <strong>Select Jobs for Batch:</strong>
              <Checkbox.Group
                style={{ width: "100%", marginTop: 8 }}
                value={selectedJobIds}
                onChange={setSelectedJobIds}
              >
                {selectedBatchSet.jobs.map((job) => (
                  <div key={job.id} style={{ marginBottom: 8 }}>
                    <Checkbox value={job.id}>
                      <strong>{job.job_number}</strong> —{" "}
                      {job.quantity?.toLocaleString()} pcs
                      {job.priority === "urgent" && (
                        <Tag color="red" style={{ marginLeft: 8 }}>
                          URGENT
                        </Tag>
                      )}
                      {job.priority === "high" && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>
                          HIGH
                        </Tag>
                      )}
                    </Checkbox>
                  </div>
                ))}
              </Checkbox.Group>
            </div>

            <div>
              <strong>Assign Machine (optional):</strong>
              <Select
                style={{ width: "100%", marginTop: 8 }}
                value={selectedMachine}
                onChange={setSelectedMachine}
                allowClear
                placeholder="Auto-assign or choose machine"
                showSearch
                optionFilterProp="children"
              >
                {machines.map((m) => (
                  <Option key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Schedule Batch Modal ───────────────────────────── */}
      <Modal
        title={`Schedule Batch: ${selectedBatch?.batch_number || ""}`}
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          setSelectedBatch(null);
          scheduleForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={scheduleForm}
          layout="vertical"
          onFinish={handleScheduleBatch}
        >
          <Form.Item
            name="machine_id"
            label="Machine"
            rules={[{ required: true, message: "Select a machine" }]}
          >
            <Select
              placeholder="Select machine"
              showSearch
              optionFilterProp="children"
            >
              {machines.map((m) => (
                <Option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="scheduled_date"
            label="Date"
            rules={[{ required: true, message: "Pick a date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="start_time"
            label="Start Time"
            rules={[{ required: true, message: "Pick start time" }]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={scheduleLoading}
              >
                Schedule
              </Button>
              <Button onClick={() => setScheduleModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BatchManager;
